import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { IoChevronDown } from "react-icons/io5";
import { IoMdShareAlt } from "react-icons/io";
import MainLayout from "@/Component/MainLayout";
import CopyCouponButton from "@/Component/Common/CopyCouponButton";
import Breadcrumbs from "@/Component/Common/Breadcrumbs";
import DownloadNH from "@/Component/Common/DownloadNH";
import HolidayPackageSection from "@/Component/Common/HolidayPackageSection";
import PageHero from "@/Component/Common/PageHero";
import ShareModal from "@/Component/Module/HolidayDetail/ShareModal";
import { fetchCouponDetail } from "@/services/api/common";
import {
  formatCouponExpiry,
  formatCouponExpiryShort,
} from "@/utils/couponHelpers";
import { withRegionPath } from "@/utils/session";
import accordionStyles from "@/Component/Common/PolicyAccordion/PolicyAccordion.module.css";
import styles from "./CouponDetail.module.css";

function DetailAccordion({ items = [] }) {
  const [openMap, setOpenMap] = useState(() => {
    const firstId = items[0]?.id;
    return firstId ? { [String(firstId)]: true } : {};
  });

  useEffect(() => {
    if (!items.length) {
      setOpenMap({});
      return;
    }

    setOpenMap((prev) => {
      const hasOpen = items.some((item) => prev[String(item.id)]);
      if (hasOpen) return prev;
      return { [String(items[0].id)]: true };
    });
  }, [items]);

  const toggleItem = (id) => {
    setOpenMap((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (!items.length) return null;

  return (
    <div className={accordionStyles.wrap}>
      {items.map((item, index) => {
        const id = String(item.id);
        const isOpen = Boolean(openMap[id]);

        return (
          <div
            key={id}
            className={`${accordionStyles.cardSection} ${index === 0 ? accordionStyles.cardSectionFirst : ""}`}
          >
            <button
              type="button"
              className={accordionStyles.cardHeader}
              aria-expanded={isOpen}
              onClick={() => toggleItem(id)}
            >
              <span className={accordionStyles.overviewLabel} />
              <h2 className={accordionStyles.cardTitle}>{item.fieldName}</h2>
              <IoChevronDown
                size={24}
                className={isOpen ? accordionStyles.chevronOpen : ""}
              />
            </button>
            {isOpen ? (
              <div className={accordionStyles.cardBody}>
                <div
                  className={accordionStyles.policyHtml}
                  dangerouslySetInnerHTML={{ __html: item.fieldValue || "" }}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function BookCard({ heading, description, buttonUrl, className = "" }) {
  if (!heading && !description && !buttonUrl) return null;

  return (
    <div className={`${styles.ctaCard} ${className}`.trim()}>
      {heading ? <h2 className={styles.ctaTitle}>{heading}</h2> : null}
      {description ? <p className={styles.ctaDesc}>{description}</p> : null}
      {buttonUrl ? (
        <a href={withRegionPath(buttonUrl)} className={styles.bookBtn}>
          Book Now
        </a>
      ) : null}
    </div>
  );
}

export default function CouponDetail({
  couponSlug = "",
  initialCouponDetail = null,
}) {
  const contentRef = useRef(null);
  const [couponDetail, setCouponDetail] = useState(initialCouponDetail);
  const [loading, setLoading] = useState(!initialCouponDetail);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState({ url: "", title: "" });

  const loadCouponDetail = useCallback(async () => {
    if (!couponSlug) return;

    setLoading(true);
    try {
      const response = await fetchCouponDetail({ couponSlug });
      setCouponDetail(response?.data || null);
    } catch (error) {
      console.error("Failed to load coupon detail:", error);
      setCouponDetail(null);
    } finally {
      setLoading(false);
    }
  }, [couponSlug]);

  useEffect(() => {
    if (initialCouponDetail) {
      setCouponDetail(initialCouponDetail);
      setLoading(false);
      return;
    }

    loadCouponDetail();
  }, [initialCouponDetail, loadCouponDetail]);

  const recommendedData = couponDetail?.recommendedData || {};
  const recommendedHoliday = recommendedData.recommendedHoliday || [];
  const recommendedActivity = recommendedData.recommendedActivity || [];
  const packageTitle =
    recommendedData.recommendedHolidayTitle || "Summer Flash Deal On Eligible Packages";
  const packageSubtitle = recommendedData.recommendedHolidayDescription || "";

  const webImageUrl =
    couponDetail?.bannerImagePath ||
    couponDetail?.mobileBannerImagePath ||
    "/images/hero.png";

  const validityLabel = useMemo(() => {
    const formatted = formatCouponExpiryShort(couponDetail?.validTo);
    return formatted ? `Till ${formatted}` : "";
  }, [couponDetail?.validTo]);

  const handleShareClick = () => {
    if (typeof window === "undefined") return;

    setShareData({
      url: window.location.href,
      title: couponDetail?.offer_Heading || "Coupon Offer",
    });
    setShowShareModal(true);
  };

  if (loading) {
    return (
      <MainLayout hideHeader>
        <div className={styles.loadingWrap}>
          <Image
            src="/assets/loading.gif"
            alt="Loading coupon details"
            width={100}
            height={100}
          />
        </div>
      </MainLayout>
    );
  }

  if (!couponDetail) {
    return (
      <MainLayout hideHeader>
        <div className={`standard-padding ${styles.emptyState}`}>
          Coupon not found.
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout hideHeader>
      <PageHero
        webImageUrl={webImageUrl}
        alt={couponDetail.offer_Heading || "Coupon banner"}
        scrollTargetRef={contentRef}
      />
      <Breadcrumbs />
      <div className={`standard-padding ${styles.contentWrap}`}>
        <BookCard
          className={styles.mobileCta}
          heading={couponDetail.offer_Heading}
          description={couponDetail.offer_Description}
          buttonUrl={couponDetail.offer_Button_URL}
        />

        <div className={styles.layout}>
          <div className={styles.mainCol}>
            <section className={styles.offerCard}>
              <span className={styles.overviewLabel}></span>
              <div className={styles.offerCardHeader}>
                <h1 className={styles.offerCardTitle}>Offer Details</h1>
                <span
                  role="button"
                  tabIndex={0}
                  className={styles.iconBtn}
                  aria-label="Share"
                  onClick={handleShareClick}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleShareClick();
                    }
                  }}
                >
                  <IoMdShareAlt size={20} />
                </span>
              </div>

              <div className={styles.detailsTable}>
                <div className={styles.tableCell}>
                  <p className={styles.tableLabel}>Coupon Code</p>
                  <div className={styles.tableLabelDiv}>
                    <div className={styles.codeBox}>
                      <span className={styles.codeText}>{couponDetail.couponCode}</span>
                      <CopyCouponButton
                        code={couponDetail.couponCode}
                        className={styles.copyBtn}
                        iconSize={12}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.tableCell}>
                  <p className={styles.tableLabel}>Offer Details</p>
                  <div className={styles.tableLabelDiv}>
                    <p className={styles.tableValue}>{couponDetail.description}</p>
                  </div>
                </div>
                <div className={styles.tableCell}>
                  <p className={styles.tableLabel}>Validity</p>
                  <div className={styles.tableLabelDiv}>
                    <p className={styles.tableValue}>
                      {validityLabel || formatCouponExpiry(couponDetail.validTo)}
                    </p>
                  </div>
                </div>
              </div>

              <div className={styles.mobileDetails}>
                <div className={styles.mobileDetailBlock}>
                  <p className={styles.tableLabel}>Coupon Code</p>
                  <div className={styles.codeBox}>
                    <span className={styles.codeText}>{couponDetail.couponCode}</span>
                    <CopyCouponButton
                      code={couponDetail.couponCode}
                      className={styles.copyBtn}
                      iconSize={12}
                    />
                  </div>
                </div>
                <div className={styles.mobileDetailBlock}>
                  <p className={styles.tableLabel}>Offer Details</p>
                  <p className={styles.tableValue}>{couponDetail.description}</p>
                </div>
                <div className={styles.mobileDetailBlock}>
                  <p className={styles.tableLabel}>Validity</p>
                  <p className={styles.tableValue}>
                    {validityLabel || formatCouponExpiry(couponDetail.validTo)}
                  </p>
                </div>
              </div>
            </section>

            <DetailAccordion items={couponDetail.details || []} />


          </div>

          <aside className={styles.sidebarCol}>
            <BookCard
              heading={couponDetail.offer_Heading}
              description={couponDetail.offer_Description}
              buttonUrl={couponDetail.offer_Button_URL}
            />
          </aside>
        </div>
      </div>
      {recommendedHoliday?.length > 0 ? (
        <div className={styles.packagesSection}>
          <HolidayPackageSection
            title={packageTitle}
            subtitle={packageSubtitle}
            data={{ holidayList: recommendedHoliday, }}
            moduleType="holiday"
          />
        </div>
      ) : null}
      {recommendedActivity?.length > 0 ? (
        <div className={styles.packagesSection}>
          <HolidayPackageSection
            title={packageTitle}
            subtitle={packageSubtitle}
            data={{ activityList: recommendedActivity }}
            moduleType="activity"
          />
        </div>
      ) : null}
      <DownloadNH />

      <ShareModal
        show={showShareModal}
        onHide={() => setShowShareModal(false)}
        shareUrl={shareData.url}
        title={shareData.title}
      />
    </MainLayout>
  );
}
